import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import { simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';

import { VALUE_NAMES } from './constants';
import { categorySegments, LIST_STATUS_DISPLAY, overallListStatus, progressSummary } from './referralProgress';
import { Tone, TONE_STYLES } from './StatusBadge';

const columnHelper = createColumnHelper<Workflow>();

// "12 min" under an hour; "1 hr 3 mins" beyond. Coarse on purpose — it's an at-a-glance age.
function elapsed(date: Date): string {
  const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min${rest === 1 ? '' : 's'}`;
}

const inboundReferralsTableColumns = () => [
  columnHelper.accessor((row) => row.patientName, {
    id: 'patientName',
    header: 'Patient Name',
    cell: (info) => <PatientLink>{info.getValue()}</PatientLink>,
    size: 160,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.valueFor(VALUE_NAMES.PLAN_TYPE)?.value ?? '', {
    id: 'planType',
    header: 'Plan Type',
    cell: (info) => info.getValue() || '—',
    size: 120,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.valueFor(VALUE_NAMES.REFERRAL_SOURCE)?.value ?? '', {
    id: 'referralSource',
    header: 'Referral Source',
    cell: (info) => info.getValue() || '—',
    size: 150,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.groupNames.join(', '), {
    id: 'snf',
    header: 'SNF',
    cell: (info) => info.getValue() || '—',
    size: 130,
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'progress',
    header: 'Progress',
    size: 190,
    cell: (info) => {
      const workflow = info.row.original;
      const { index, total, categoryName } = progressSummary(workflow);
      return (
        <ProgressCell>
          <Bar data-testid='progress-bar'>
            {categorySegments(workflow).map((segment) => (
              <Segment key={segment.key} $color={TONE_STYLES[LIST_STATUS_DISPLAY[segment.status].tone].accent} />
            ))}
          </Bar>
          <ProgressLabel>{total > 0 ? `${index} of ${total} – ${categoryName}` : '—'}</ProgressLabel>
        </ProgressCell>
      );
    },
  }),
  columnHelper.display({
    id: 'currentStatus',
    header: 'Current Status',
    size: 140,
    cell: (info) => {
      const status = overallListStatus(info.row.original);
      const display = LIST_STATUS_DISPLAY[status];
      return (
        <Pill $tone={display.tone} data-testid='list-status'>
          {status === 'processing' && <AISparkleIcon width={12} height={12} color={TONE_STYLES[display.tone].color} />}
          {display.label}
        </Pill>
      );
    },
  }),
  columnHelper.accessor((row) => row.createdAt, {
    id: 'timeElapsed',
    header: 'Time Elapsed',
    size: 150,
    enableSorting: false,
    cell: (info) => {
      const created = info.getValue() as Date | null;
      if (!created) return '—';
      return (
        <TimeCell>
          <Duration>{elapsed(created)}</Duration>
          <Timestamp>{simpleDateAtTime(created)}</Timestamp>
        </TimeCell>
      );
    },
  }),
];

export default inboundReferralsTableColumns;

const PatientLink = styled.span`
  font-weight: 700;
  color: ${colors.primaryBlue};
`;

const ProgressCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Bar = styled.div`
  display: flex;
  gap: 4px;
`;

const Segment = styled.div<{ $color: string }>`
  height: 9px;
  flex: 1;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

const ProgressLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;

const TimeCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const Duration = styled.span`
  color: ${colors.black};
`;

const Timestamp = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;
